<template>
  <!-- 사이드 네비게이션 드로어 -->
  <v-navigation-drawer
    v-model="drawer"
    :permanent="!isMobile"
    color="transparent"
    width="240"
    :border="0"
    class="argong-sidebar"
  >
    <!-- 로고 -->
    <!-- 로고 클릭 시 홈("/" = 구 학교관리) 대신 목록 > 유료 탭으로 이동 (qa-feedback-1020) -->
    <router-link to="/school-browse/paid" class="sidebar-logo-link" aria-label="알공 관리자 홈">
      <span class="sidebar-logo-text">알공 관리자</span>
    </router-link>

    <v-divider class="argong-sidebar-divider" />

    <v-list density="compact" nav class="pt-2 px-2 argong-nav-list">
      <!-- 계정등록 (구 USER 계정관리) -->
      <div class="nav-section-label">계정등록</div>

      <v-list-item
        v-for="item in userMenuItems"
        :key="item.num"
        :to="item.path"
        rounded="0"
        class="nav-list-item"
        active-class="nav-list-item--active"
        @click="handleNavClick"
      >
        <template #prepend>
          <v-icon :icon="item.icon" size="17" class="mr-1" />
        </template>
        <v-list-item-title class="nav-item-text">
          {{ item.title }}
          <span v-if="item.isNew" class="new-badge">NEW</span>
          <span v-if="item.hasAlert && newIssue" class="alert-dot" />
        </v-list-item-title>
      </v-list-item>

      <v-divider class="argong-sidebar-divider my-2" />

      <!-- 목록 (구 학교 조회) -->
      <div class="nav-section-label nav-section-label--row">
        <span>목록</span>
        <!-- 전 분류 통합 연락처 다운로드 + 출처 선택 (new-requests-1018 #1) -->
        <v-menu location="bottom end">
          <template #activator="{ props: menuProps }">
            <button type="button" class="nav-section-action" v-bind="menuProps">
              <v-icon icon="mdi-download" size="12" class="mr-1" />연락처 다운로드
            </button>
          </template>
          <v-list density="compact">
            <v-list-item @click="downloadAllContacts('all')">
              <v-list-item-title>전체 (LMS 인증 + 수동 등록)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="downloadAllContacts('lms')">
              <v-list-item-title>LMS 인증 연락처만</v-list-item-title>
            </v-list-item>
            <v-list-item @click="downloadAllContacts('manual')">
              <v-list-item-title>수동 등록 연락처만</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <div class="nav-global-search">
        <v-autocomplete
          :key="globalSearchResetKey"
          v-model="globalSearchQuery"
          v-model:search="globalSearchText"
          :items="globalSearchItems"
          :loading="globalSearchLoading"
          append-inner-icon="mdi-magnify"
          density="compact"
          variant="outlined"
          menu-icon=""
          color="primary"
          placeholder="학교명 또는 코드 검색"
          spellcheck="false"
          hide-details
          hide-no-data
          @focus="ensureGlobalSearchData"
          @keydown.enter.prevent="onGlobalSearchEnter"
          no-filter
          @update:model-value="goToGlobalSearchMatch"
        />
      </div>

      <v-list-item
        v-for="item in browseMenuItems"
        :key="item.path"
        :to="item.path"
        rounded="0"
        class="nav-list-item"
        active-class="nav-list-item--active"
        @click="handleNavClick"
      >
        <template #prepend>
          <v-icon :icon="item.icon" size="17" class="mr-1" />
        </template>
        <v-list-item-title class="nav-item-text">
          {{ item.title }}
          <span v-if="item.isNew" class="new-badge">NEW</span>
        </v-list-item-title>
      </v-list-item>

      <v-divider class="argong-sidebar-divider my-2" />

      <!-- 사용 현황 -->
      <v-list-item
        to="/usage-status"
        rounded="0"
        class="nav-list-item"
        active-class="nav-list-item--active"
        @click="handleNavClick"
      >
        <template #prepend>
          <v-icon icon="mdi-chart-donut" size="17" class="mr-1" />
        </template>
        <v-list-item-title class="nav-item-text">
          사용 현황
        </v-list-item-title>
      </v-list-item>
    </v-list>

    <!-- 로그아웃 — 사이드바 좌측 하단 고정 (박스 없이 텍스트만) -->
    <template #append>
      <div class="sidebar-logout">
        <button type="button" class="sidebar-logout__link" @click="logout">
          로그아웃
        </button>
      </div>
    </template>
  </v-navigation-drawer>

  <!-- 상단 앱바 -->
  <v-app-bar flat height="64" color="surface" border="b">
    <v-app-bar-nav-icon
      @click.stop="drawer = !drawer"
      color="primary"
    />
    <v-app-bar-title class="d-md-none">
      <span class="text-body-1 font-weight-bold app-bar-title">알공 관리자</span>
    </v-app-bar-title>
    <v-spacer />
    <div class="d-flex align-center pr-3 gap-3">
      <v-btn
        variant="outlined"
        color="primary"
        size="small"
        density="comfortable"
        @click="openAiRequest"
      >
        <v-icon icon="mdi-robot-outline" size="16" start />
        AI 요청
      </v-btn>
    </div>
  </v-app-bar>

  <!-- AI 요청 팝업 — 배경을 막는 모달이 아니라, 뒤쪽 페이지도 그대로 조작 가능한
       독립 플로팅 창으로 띄운다. 뒤쪽 콘텐츠를 가리지 않도록 폭을 좁게 잡고,
       위치도 상단 손잡이를 드래그해 옮길 수 있게 한다. 클릭 시점에만 마운트. -->
  <div
    v-if="aiRequestOpen"
    class="ai-request-float"
    :style="{ left: aiPanelLeft + 'px', top: aiPanelTop + 'px' }"
    @keydown.esc="aiRequestOpen = false"
  >
    <div
      class="ai-request-float__handle"
      @pointerdown="startDragAiPanel"
    >
      <span class="ai-request-float__handle-label">
        <v-icon icon="mdi-drag" size="16" />
        끌어서 위치 이동
      </span>
      <div class="ai-request-float__handle-actions">
        <button
          type="button"
          class="ai-request-float__reset"
          @click="resetAiChat"
        >
          <v-icon icon="mdi-refresh" size="14" />
          초기화
        </button>
        <button
          type="button"
          class="ai-request-float__close"
          aria-label="닫기"
          @click="aiRequestOpen = false"
        >
          <v-icon icon="mdi-close" size="18" />
        </button>
      </div>
    </div>
    <div class="ai-request-float__body">
      <NlpAccountCreate ref="nlpAccountCreate" />
    </div>
  </div>

  <!-- 메인 콘텐츠 -->
  <!-- scrollable: 앱바/사이드바는 고정, 아래 컨텐츠 영역 안에서만 스크롤되도록
       (기존엔 v-main이 콘텐츠 높이만큼 늘어나 문서 전체가 스크롤됨) -->
  <v-main scrollable>
    <v-container fluid class="pa-4 pa-sm-6 app-content">
      <!-- AI 요청(NlpAccountCreate)만 keep-alive — 다른 탭에 다녀와도 대화/입력
           상태가 유지된다 (초기화 버튼을 누르기 전까지). 새로고침 시에는 초기화됨. -->
      <router-view v-slot="{ Component }">
        <keep-alive include="NlpAccountCreate">
          <component :is="Component" />
        </keep-alive>
      </router-view>
    </v-container>
  </v-main>
</template>

<script>
import { gettransferstudentlength, getschoollist } from "@/api/user-api-list";
import { instanceAuth } from "@/api/url";
import { getSchoolCreate } from "@/api/guest-api-list";
import { resolveCategory } from "@/constants/schoolType";
import NlpAccountCreate from "@/components/userAccountManage/accountIssue/NlpAccountCreate.vue";

export default {
  name: "DefaultLayout",
  components: { NlpAccountCreate },
  data() {
    return {
      drawer: window.innerWidth >= 960,
      windowWidth: window.innerWidth,
      newIssue: false,
      // 앱바 우측 "AI 요청" — 클릭 시 페이지 이동 대신 팝업으로 연다.
      // 뒤쪽 콘텐츠를 가리지 않게 손잡이로 끌어서 옮길 수 있어, 위치를
      // 픽셀 좌표(aiPanelLeft/Top)로 직접 관리한다.
      aiRequestOpen: false,
      aiPanelLeft: null,
      aiPanelTop: 76,
      aiPanelWidth: 420,
      aiDragging: false,
      aiDragOffsetX: 0,
      aiDragOffsetY: 0,
      // 사이드바 전역 학교 검색(목록 > 유료 위) — 학교명/코드로 검색하면 해당
      // 카테고리(유료/무료/연수/서포터즈/AI/박람회) 목록으로 바로 이동한다.
      globalSearchQuery: null,
      globalSearchText: "",
      // v-autocomplete 자체를 강제로 새 인스턴스로 마운트해 입력창 표시 텍스트까지
      // 확실히 비우기 위한 key (검색 이동 후 증가시킨다).
      globalSearchResetKey: 0,
      globalSearchSchools: [],
      globalSearchLoaded: false,
      globalSearchLoading: false,
      // USER 계정관리 상단 — 계정 발급 / AI 요청 / 전학생 추가발급 순으로 우선 노출
      userMenuItems: [
        {
          num: 2,
          title: "계정 발급",
          path: "/user/account-issue",
          icon: "mdi-account-plus-outline",
        },
        {
          num: 7,
          title: "계정 수동 발급",
          path: "/guest/free-application-manual",
          icon: "mdi-clipboard-edit-outline",
        },
        {
          num: 4,
          title: "전학생 추가발급",
          path: "/user/add-transfer-student",
          icon: "mdi-account-arrow-right-outline",
          hasAlert: true,
        },
      ],
      // USER 계정관리 나머지 — 학교 조회 그룹 아래로 이어서 노출
      browseMenuItems: [
        {
          title: "유료",
          path: "/school-browse/paid",
          icon: "mdi-credit-card-outline",
        },
        {
          title: "무료",
          path: "/school-browse/free",
          icon: "mdi-gift-outline",
        },
        {
          title: "연수",
          path: "/school-browse/training",
          icon: "mdi-teach",
        },
        {
          title: "서포터즈",
          path: "/school-browse/supporters",
          icon: "mdi-account-heart-outline",
        },
        {
          title: "AI미래교육연구회",
          path: "/school-browse/ai",
          icon: "mdi-brain",
        },
        {
          title: "박람회",
          path: "/school-browse/expo",
          icon: "mdi-store-outline",
        },
      ],
    };
  },
  computed: {
    isMobile() {
      return this.windowWidth < 960;
    },
    // "학교명 (코드)" 형태의 자동완성 후보 — 기존 학교 검색 자동완성(searchlist)과 동일한 표기.
    globalSearchItems() {
      const query = (this.globalSearchText || "").trim();
      if (!query) return [];
      const normalized = query.toLowerCase();
      return this.browseMenuItems.flatMap((item) => {
        const category = item.path.split("/").pop();
        const count = this.globalSearchSchools.filter((school) =>
          school.category === category &&
          [school.schoolName, school.schoolCode].some((value) =>
            String(value || "").toLowerCase().includes(normalized)
          )
        ).length;
        return count ? [{
          title: `${item.title} · ${count}건`,
          value: JSON.stringify({ category, query }),
        }] : [];
      });
    },
  },
  async created() {
    if (this.$store.state.token) {
      await this.fetchAlerts();
    }
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("pointermove", this.onDragAiPanel);
    window.removeEventListener("pointerup", this.stopDragAiPanel);
  },
  methods: {
    logout() {
      sessionStorage.removeItem("token");
      location.href = window.location.origin;
    },
    handleNavClick() {
      if (this.isMobile) {
        this.drawer = false;
      }
    },
    // 전 분류(유료+체험) 통합 연락처 다운로드 — category=all.
    // source: all(전체) / lms(LMS 인증만) / manual(수동 등록만)
    // 기존엔 운영 URL 하드코딩 + 파라미터 없음이라 실제로는 유료만 내려가고 있었다 (new-requests-1018 #1)
    downloadAllContacts(source = "all") {
      window.open(
        `${instanceAuth.defaults.baseURL}/schools/download/contact?category=all&source=${source}`
      );
    },
    openAiRequest() {
      // 처음 열 때만 기본 위치(우측 상단 근처)를 잡는다 — 이후엔 사용자가
      // 끌어다 둔 위치를 다음에 열 때도 그대로 기억한다.
      if (this.aiPanelLeft === null) {
        this.aiPanelLeft = Math.max(20, window.innerWidth - this.aiPanelWidth - 20);
      }
      this.aiRequestOpen = true;
    },
    resetAiChat() {
      this.$refs.nlpAccountCreate?.resetChat();
    },
    startDragAiPanel(e) {
      // 초기화/닫기 버튼 클릭까지 드래그로 잡아먹지 않도록 버튼 클릭은 제외한다.
      if (e.target.closest("button")) return;
      this.aiDragging = true;
      this.aiDragOffsetX = e.clientX - this.aiPanelLeft;
      this.aiDragOffsetY = e.clientY - this.aiPanelTop;
      window.addEventListener("pointermove", this.onDragAiPanel);
      window.addEventListener("pointerup", this.stopDragAiPanel);
    },
    onDragAiPanel(e) {
      if (!this.aiDragging) return;
      const maxLeft = Math.max(0, window.innerWidth - 80);
      const maxTop = Math.max(0, window.innerHeight - 60);
      this.aiPanelLeft = Math.min(Math.max(0, e.clientX - this.aiDragOffsetX), maxLeft);
      this.aiPanelTop = Math.min(Math.max(0, e.clientY - this.aiDragOffsetY), maxTop);
    },
    stopDragAiPanel() {
      this.aiDragging = false;
      window.removeEventListener("pointermove", this.onDragAiPanel);
      window.removeEventListener("pointerup", this.stopDragAiPanel);
    },
    onResize() {
      this.windowWidth = window.innerWidth;
      if (!this.isMobile) {
        this.drawer = true;
      }
    },
    async fetchAlerts() {
      const res = await gettransferstudentlength();
      this.newIssue = !!(res?.data?.data);
    },
    // 전역 검색용 학교 목록 로드 — 학교 조회(school-browse) 화면의 fetchAll()과
    // 동일한 방식(유료+체험 전체 조회 후 client-side 분류)으로 최초 1회만 가져온다.
    async ensureGlobalSearchData() {
      if (this.globalSearchLoaded || this.globalSearchLoading) return;
      this.globalSearchLoading = true;
      try {
        const [paidRes, guestRes] = await Promise.all([
          getschoollist(),
          getSchoolCreate(),
        ]);
        const paid = (paidRes?.data?.data ?? []).map((s) => ({
          schoolCode: s.schoolCode,
          schoolName: `${s.location ?? ""} ${s.schoolName}초등학교`.trim(),
          category: resolveCategory({ ...s, schoolLevel: s.schoolLevel ?? "user" }),
        }));
        const guest = (guestRes?.data?.data ?? []).map((s) => ({
          schoolCode: s.schoolCode,
          schoolName: s.schoolName,
          category: resolveCategory({ ...s, schoolLevel: s.schoolLevel ?? "guest" }),
        }));
        this.globalSearchSchools = [...paid, ...guest];
        this.globalSearchLoaded = true;
      } finally {
        this.globalSearchLoading = false;
      }
    },
    onGlobalSearchEnter() {
      const current = this.$route.params.category;
      const items = this.globalSearchItems;
      const item = items.find((item) => JSON.parse(item.value).category === current) || items[0];
      if (item) this.goToGlobalSearchMatch(item.value);
    },
    goToGlobalSearchMatch(text) {
      if (!text) return;
      const { category, query } = JSON.parse(text);
      this.$router.push({ path: `/school-browse/${category}`, query: { q: query } });
      this.globalSearchQuery = null;
      this.globalSearchText = "";
      this.globalSearchResetKey += 1; // 입력창 표시 텍스트까지 확실히 비우기 위해 새로 마운트
      if (this.isMobile) this.drawer = false;
    },
  },
  watch: {
    async $route() {
      if (this.$store.state.token) {
        await this.fetchAlerts();
      }
    },
  },
};
</script>

<style scoped>
/* v-main(scrollable)의 스크롤 영역(.v-main__scroller) 안에서 컨테이너가 그
   높이를 100% 채우도록 — 페이지 컴포넌트가 height:100%로 내부 스크롤을
   구성할 수 있게 하는 기반. (v-container 자체는 overflow를 걸지 않으므로
   height:100%를 안 쓰는 일반 페이지는 기존과 동일하게 자연스럽게 넘친 만큼
   .v-main__scroller가 스크롤 처리한다.) */
.app-content {
  height: 100%;
}

/* 사이드바 — design-guide §5.6 */
.argong-sidebar {
  background: var(--bg-subtle) !important;
}

.sidebar-logo-link {
  display: inline-flex;
  align-items: center;
  padding: var(--space-2) var(--space-3);
  margin: 12px 12px 10px;
  transition: opacity 0.15s ease;
  text-decoration: none;
}
.sidebar-logo-link:hover { opacity: 0.85; }
.sidebar-logo-link:active { opacity: 0.7; }
.sidebar-logo-link:focus-visible {
  outline: 2px solid var(--color-primary-600);
  outline-offset: 4px;
  border-radius: var(--radius-md);
}
.sidebar-logo-text {
  font-family: var(--font-family-base);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: var(--text-primary);
}

.argong-sidebar-divider {
  border-color: var(--border-default) !important;
  opacity: 1 !important;
}

.nav-section-label {
  color: var(--text-tertiary);
  font-size: 11px;
  font-family: var(--font-family-base);
  font-weight: 700;
  letter-spacing: 0;
  padding: 10px 12px 4px;
}
.nav-section-label--row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}
.nav-section-action {
  display: inline-flex;
  align-items: center;
  color: var(--color-primary-700);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  white-space: nowrap;
}
.nav-section-action:hover {
  color: var(--color-primary-900);
  text-decoration: underline;
}

.nav-global-search {
  padding: 2px 12px 10px;
}
.nav-global-search :deep(.v-field) {
  background: var(--bg-surface);
}

.sidebar-logout {
  padding: var(--space-3) 12px;
  border-top: 1px solid var(--border-default);
}
.sidebar-logout__link {
  display: block;
  width: 100%;
  text-align: center;
  background: none;
  border: none;
  padding: 4px 0;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  font-family: var(--font-family-base);
  cursor: pointer;
}
.sidebar-logout__link:hover {
  color: var(--color-primary-700);
  text-decoration: underline;
}

/* AI 요청 — 모달이 아닌 독립 플로팅 창. 배경(overlay)이 없어 뒤쪽 페이지를
   그대로 스크롤/클릭할 수 있고, 폭도 좁게 잡아 뒤쪽 콘텐츠를 최대한 덜
   가린다. 위치는 상단 손잡이를 끌어서(pointer drag) 자유롭게 옮길 수 있다. */
.ai-request-float {
  position: fixed;
  width: min(420px, calc(100vw - 40px));
  height: min(600px, calc(100vh - 96px));
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg, 0 12px 32px rgba(0, 0, 0, 0.2));
  z-index: 2000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ai-request-float__handle {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: 6px 8px 6px 12px;
  background: var(--bg-subtle);
  border-bottom: 1px solid var(--border-default);
  cursor: move;
  user-select: none;
  touch-action: none;
}
.ai-request-float__handle-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary);
}
.ai-request-float__handle-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
}
.ai-request-float__reset {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 8px;
  border-radius: var(--radius-md);
  border: 1px solid var(--border-default);
  background: var(--bg-surface);
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.ai-request-float__reset:hover {
  background: var(--bg-muted);
  color: var(--text-primary);
}
.ai-request-float__close {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid var(--border-default);
  background: var(--bg-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);
}
.ai-request-float__close:hover {
  background: var(--bg-muted);
  color: var(--text-primary);
}
.ai-request-float__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-4);
}
.ai-request-float__body :deep(.nlp-page) {
  height: 100%;
}

/* §5.6 메뉴 항목: height 36px, 좌패딩 12px */
.nav-list-item {
  color: var(--text-secondary) !important;
  font-family: var(--font-family-base);
  margin-bottom: 1px;
  min-height: 36px !important;
  padding-left: 12px !important;
  padding-right: 8px !important;
  position: relative;
  border-radius: 0 !important;
}
/* prepend(아이콘) 우측 간격 축소 — 텍스트 공간 확보 */
.nav-list-item :deep(.v-list-item__prepend) {
  margin-inline-end: 6px !important;
}

/* 비액티브 hover: --bg-muted */
.nav-list-item:hover {
  background: var(--bg-muted) !important;
}

/* 액티브: 좌측 3px Primary 바 + --color-primary-50 배경 + text-primary + 600 */
.nav-list-item.nav-list-item--active,
.nav-list-item.v-list-item--active {
  background: var(--color-primary-50) !important;
  color: var(--text-primary) !important;
  font-weight: 600;
}

.nav-list-item.nav-list-item--active::before,
.nav-list-item.v-list-item--active::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--color-primary-600);
  opacity: 1;
}

/* Vuetify의 기본 active overlay 비활성화 — 위 배경색만 보이게 */
.nav-list-item.v-list-item--active :deep(.v-list-item__overlay) {
  opacity: 0 !important;
}

.nav-item-text {
  font-size: 14px !important;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: visible !important;
}
.nav-item-text > :first-child,
.nav-list-item :deep(.v-list-item-title) {
  overflow: visible !important;
  text-overflow: clip !important;
}

.new-badge {
  display: inline-flex;
  align-items: center;
  background: var(--color-primary-600);
  color: var(--text-on-primary);
  font-size: 8.5px;
  line-height: 1;
  font-weight: 700;
  padding: 2px 4px;
  border-radius: var(--radius-sm);
  flex-shrink: 0;
  letter-spacing: 0;
}

.alert-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  background: var(--color-error-text);
  border-radius: 50%;
  flex-shrink: 0;
}

/* 앱바 */
.app-bar-title {
  color: var(--color-primary-900);
}

.admin-name {
  color: var(--text-secondary);
  font-family: var(--font-family-base);
}
</style>
